import { portalPath } from '../../utils/portalPath';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, Menu, Search, X, Droplets } from 'lucide-react';
import { Navigation, useNavigation } from './Sidebar';
import { useMediaQuery } from './useMediaQuery';
import GlobalSearch from './GlobalSearch';
import HelpMenu from '../Help/HelpMenu';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsService } from '../../services';
import { NOTIFICATIONS_CHANGED } from '../../services/notifications';
import './Header.css';

const UNREAD_POLL_MS = 60_000;

function getInitials(name: string | undefined): string {
  if (!name || !name.trim()) return 'DU';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
  }
  return name.slice(0, 2).toUpperCase();
}

function getRoleLabel(role: string | undefined): string {
  if (!role) return 'Consultant';
  const labels: Record<string, string> = {
    consultant: 'Consultant',
    facility_manager: 'Facility manager',
    client: 'Client',
  };
  return labels[role] ?? role;
}

interface HeaderProps {
  onMenuClick: () => void;
  menuOpen?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, menuOpen }) => {
  const navigate = useNavigate();
  const { navItems, badge } = useNavigation();
  const mobile = useMediaQuery('(max-width: 1023px)');
  const small = useMediaQuery('(max-width: 640px)');
  const compactSearch = useMediaQuery('(max-width: 1199px)');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchButton = useRef<HTMLButtonElement>(null);
  const searchPanel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchOpen) searchPanel.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [searchOpen]);
  useEffect(() => setSearchOpen(false), [compactSearch]);
  const closeSearch = () => { setSearchOpen(false); searchButton.current?.focus(); };

  const location = useLocation();
  const { currentUser, signOut, bootstrap } = useAuth();
  const displayName = currentUser?.name ?? '';
  const initials = getInitials(currentUser?.name);
  const roleLabel = getRoleLabel(currentUser?.role);

  const [accountOpen,setAccountOpen] = useState(false);
  const account = useRef<HTMLDivElement>(null);
  const avatar = useRef<HTMLButtonElement>(null);
  const portal = location.pathname.startsWith('/portal') || ['client','facility_manager'].includes(currentUser?.role || '');
  const admin = currentUser?.role === 'h2okay_admin';
  useEffect(() => {
    if (!accountOpen) return;
    account.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const outside = (e: MouseEvent) => { if (!account.current?.contains(e.target as Node)) setAccountOpen(false); };
    document.addEventListener('mousedown',outside);
    return ()=>document.removeEventListener('mousedown',outside);
  },[accountOpen]);
  useEffect(()=>setAccountOpen(false),[location.pathname,location.search]);
  const go = (path: string) => { setAccountOpen(false); navigate(path); };
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationsPath = location.pathname.startsWith('/portal')
    ? '/portal/notifications'
    : '/notifications';

  const refreshUnread = useCallback(async () => {
    try {
      const { count } = await notificationsService.unreadCount();
      setUnreadCount(count);
    } catch {
      // Bell badge is best-effort; never surface polling errors.
    }
  }, []);

  useEffect(() => { if (bootstrap) setUnreadCount(bootstrap.unread_count); }, [bootstrap]);

  // Bootstrap supplies the initial count; writes and polling refresh it.
  useEffect(() => {
    const interval = setInterval(refreshUnread, UNREAD_POLL_MS);
    window.addEventListener(NOTIFICATIONS_CHANGED, refreshUnread);
    return () => { clearInterval(interval); window.removeEventListener(NOTIFICATIONS_CHANGED, refreshUnread); };
  }, [refreshUnread]);

  return (
    <header className="header">
      <div className="header-left">
        {mobile && <button
          className="menu-btn"
          onClick={onMenuClick}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="navigation-menu"
        >
          <Menu size={20} />
        </button>}
        <div className="logo"><Droplets size={24} aria-hidden="true" /><span className="logo-text">H2Okay</span></div>
        {badge && <span className="nav-view-badge">{small && badge === 'Client view' ? 'Client' : badge}</span>}
        {!mobile && <Navigation items={navItems} label="Main navigation" className="top-nav" />}
        {compactSearch && <button ref={searchButton} className="menu-btn search-toggle" aria-label="Open search" aria-expanded={searchOpen} aria-controls="header-search" onClick={() => setSearchOpen(!searchOpen)}><Search size={20} /></button>}
        {(!compactSearch || searchOpen) && <div id="header-search" ref={searchPanel} className={compactSearch ? 'search-overlay' : 'header-search'} onKeyDown={event => { if (event.key === 'Escape') closeSearch(); }}>

        <GlobalSearch portalMode={location.pathname.startsWith('/portal')}
          placeholder={['client', 'facility_manager'].includes(currentUser?.role || '') ? 'Search your facilities' : 'Search clients, facilities, assets...'}
          onResultClick={(result) => {
            setSearchOpen(false);
            navigate(location.pathname.startsWith('/portal') ? portalPath(result.path) : result.path);
          }}
        />
        {compactSearch && <button className="menu-btn" aria-label="Close search" onClick={closeSearch}><X size={20} /></button>}
        </div>}
      </div>

      <div className="header-right">
        <HelpMenu />
        <button
          className="notification-btn"
          aria-label="Notifications"
          data-tour="notifications"
          onClick={() => navigate(notificationsPath)}
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        <div className="user-menu" ref={account}>
          <button ref={avatar} className="user-avatar" aria-label="Account menu" data-tour="account-menu" aria-haspopup="menu" aria-expanded={accountOpen} onClick={()=>setAccountOpen(!accountOpen)} onKeyDown={e=>{if(['ArrowDown','ArrowUp'].includes(e.key)){e.preventDefault();setAccountOpen(true);}}}>{initials}</button>
          <button type="button" className="user-info desktop-only" aria-hidden="true" tabIndex={-1} onClick={()=>setAccountOpen(!accountOpen)}><div className="user-name">{displayName}</div><div className="user-role">{roleLabel}</div></button>
          {accountOpen && <div role="menu" aria-label="Account" className="account-dropdown" onKeyDown={e=>{
            const items=Array.from(account.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
            const index=items.indexOf(document.activeElement as HTMLButtonElement);
            if(e.key==='Escape'){setAccountOpen(false);avatar.current?.focus();}
            else if(e.key==='Tab')setAccountOpen(false);
            else if(['ArrowDown','ArrowUp','Home','End'].includes(e.key)){e.preventDefault();items[e.key==='Home'?0:e.key==='End'?items.length-1:(index+(e.key==='ArrowDown'?1:-1)+items.length)%items.length]?.focus();}
          }}>
            <p>{displayName}<br/><small>{roleLabel}</small></p>
            <button role="menuitem" onClick={()=>go(portal?'/portal/profile':admin?'/admin/profile':'/profile')}>Profile</button>
            {!portal && <button role="menuitem" onClick={()=>go('/settings')}>Settings</button>}
            {!portal && (admin || currentUser?.role==='consultant') && <button role="menuitem" onClick={()=>go(admin?'/admin/invites':'/settings?tab=invites')}>Invite users</button>}
            <button role="menuitem" onClick={()=>go(portal?'/portal/whats-new':'/whats-new')}>What's new</button>
            <button role="menuitem" onClick={()=>{setAccountOpen(false);window.dispatchEvent(new Event('h2okay:start-help-tour'));}}>Help tour</button>
            <button role="menuitem" onClick={()=>{setAccountOpen(false);void signOut();}}>Sign out</button>
          </div>}
        </div>
      </div>
    </header>
  );
};

export default Header;
